import { parse } from 'date-fns';
// @ts-ignore
import { all, spread } from 'axios';
import {
  authorizedInstance as axiosRb,
  authorizedPyInstance as axiosPy,
} from '~/utils';
import { ruby } from '~/utils/core/factories';

type TPatientDiagnosis = {
  diagnosis: { diagnosisUid: string };
  status: { statusUid: string };
  patient?: { id: string };
  provider?: { id: string };
  onsetDate: string;
  region?: { id: string };
  isPrimaryDiagnosis: boolean;
  note?: string;
};

const serializeOne = (values: TPatientDiagnosis) => {
  return {
    data: {
      ref_diagnoses_ids: [values.diagnosis.diagnosisUid],
      status_uid: values.status.statusUid,
      links: {
        patient: { linkage: { id: values.patient?.id } },
        provider: { linkage: { id: values.provider?.id } },
      },
      onset_date: values.onsetDate,
      region_uid: values.region?.id,
      is_primary_diagnosis: values.isPrimaryDiagnosis,
      note: values.note,
    },
  };
};

const normalizeOne = (data: any) => {
  return {
    ...data,
    id: data.id,
    diagnosis: {
      diagnosisUid: data.relationships.diagnosis?.id,
      diagnosisLabel: data.relationships.diagnosis?.attributes.diagnosisLabel,
    },
    region: {
      id: data.relationships.region?.id,
      attributes: {
        regionLabel: data.relationships.region?.attributes.regionLabel,
      },
    },
    onsetDate: parse(data.attributes.onsetDate),
    status: {
      statusUid: data.relationships.status?.relationships.status.data.id,
      statusLabel: data.relationships.status?.attributes.referenceStatus.statusLabel,
    },
    provider: {
      id: data.relationships.provider?.id,
      attributes: {
        displayName: data.relationships.provider?.attributes.displayName,
      },
    },
    isPrimaryDiagnosis: data.attributes.isPrimaryDiagnosis,
  };
};

const fetchOne = (diagnosisUid: string) => {
  return axiosRb
    .get(`diagnoses/${diagnosisUid}`)
    .then((r: any) => normalizeOne(r.data.data));
};

const fetchAll = (patientUid: string) => {
  return axiosRb
    .get(`patients/${patientUid}/diagnoses`)
    .then((r: any) => r.data.data.map(normalizeOne));
};

const getFormData = () => {
  return axiosPy
    .get('/api/data/reference/reference-event?event-type-name=Diagnosis')
    .then((r: any) => r.data.data)
    .then((r: any) => {
      const diagnosisEventUid = r[0].id;

      return all([
        axiosRb.get(`reference/events/${diagnosisEventUid}/diagnosis?include_type=false`),
        axiosRb.get('providers'),
        axiosRb.get(`reference/events/${diagnosisEventUid}/status`),
      ])
        .then(spread((diagnoses: any, providers: any, statuses: any) => {
          return {
            diagnoses: diagnoses.data.diagnoses,
            providers: providers.data.data,
            statuses: statuses.data.statuses,
          };
        }));
    })
    .catch((e: any) => {
      console.error('e:', e);
      console.error('Possible configuration error: no row in config.reference_event where event_type_name = "Diagnosis"');
    });
};

const getRegions = (referenceDiagnosisUid: string) => {
  if (!referenceDiagnosisUid) return [];

  return axiosRb
    .get(`reference-data/region?source=reference-diagnosis&source_uid=${referenceDiagnosisUid}`)
    .then((r: any) => r.data.data);
};

const store = {
  getFormData,
  getRegions,

  fetchAll,
  fetchOne,

  ...ruby('/diagnoses', {
    serializeOne,
    normalizeOne: (r: any) => normalizeOne(r.data.data),
  }),
};

export { store };
